/**
 * Única puerta del SPA hacia el servidor.
 *
 * Cuatro propiedades que ningún llamador puede saltarse, porque no hay otra forma de llamar
 * a la API en toda la aplicación:
 *
 * 1. **Rutas relativas al mismo origen, siempre.** Se pide `/api/v1/dashboard`, nunca un
 *    origen absoluto. De ahí que `credentials: 'same-origin'` alcance y que la cookie de
 *    sesión viaje con `SameSite=Strict`, el ajuste más estricto que existe, sin CORS ni
 *    comprobante previo. Un camino absoluto se rechaza aquí mismo, antes de la red: es la
 *    diferencia entre una regla y una convención.
 * 2. **Las cabeceras del contrato en cada petición.** La marca de petición de la aplicación,
 *    que es lo que el guardián de falsificación entre sitios exige en toda mutación, y la
 *    versión del contrato, que es lo que permite al Worker responder `409` a un paquete
 *    obsoleto en lugar de fallar de forma opaca.
 * 3. **Configuración regional fijada**, reexportada desde aquí para que las vistas tengan un
 *    solo módulo del que importar tanto la llamada como el formato: español de Chile y zona
 *    de Chile continental, sin depender del huso del navegador.
 * 4. **Dos clases de fallo, distinguibles.** `ErrorDeApi` es una respuesta del servidor con
 *    código estable; `ErrorDeRed` es que la petición no llegó. La diferencia importa porque
 *    el criterio 26.8 exige conservar lo escrito en el formulario cuando falla la
 *    conectividad, y para eso hay que poder reconocer ese caso.
 *
 * **La reacción a cada código vive aquí y no en las vistas.** Es la quinta propiedad, y la
 * agregó la tarea 4.11: `401` de sesión perdida, `428` de consentimiento pendiente, `409` de
 * cliente obsoleto y fallo de red se despachan en {@link pedir}, de modo que ninguna vista
 * pueda olvidarse de uno. Lo que el módulo **no** hace es decidir qué se ve: navegar, abrir el
 * flujo de aceptación o mostrar un aviso son cosas de React, así que se declaran como
 * {@link ReaccionesDeApi} y las instala el armazón con {@link instalarReaccionesDeApi}. Sin
 * nada instalado el comportamiento es el de antes: el error se lanza y el llamador decide.
 *
 * El `428` es el único que además **reintenta**: si la reacción resuelve que la persona aceptó
 * el consentimiento, la petición original se repite una vez, que es lo que el diseño llama
 * volver a la acción original. Los otros tres avisan y lanzan; reintentar una autorización o
 * una validación solo repite el error.
 *
 * **Dos rutas de la familia de glicemias no hablan JSON, y por eso el módulo tiene dos puertas
 * más** (tarea 5.9). La importación de CSV manda el archivo como cuerpo, así que se declara con
 * {@link CuerpoCrudo} en lugar de serializarse; la exportación responde con el archivo o con la
 * constancia de que se está armando, así que se pide con `apiClient.respuesta` y el llamador lee el
 * cuerpo. Las dos pasan por el **mismo** núcleo que el resto —{@link pedirRespuesta}—, de modo que
 * no hay una segunda vía a la API que pueda olvidarse del `401` o del aviso de conectividad.
 */

import {
  CABECERAS,
  CODIGO_DE_CLIENTE_OBSOLETO,
  CODIGOS_DE_SESION_PERDIDA,
  IDIOMA_CHILE,
  PREFIJO_API,
  SHARED_CONTRACT_VERSION,
  type SobreDeError,
} from '@aisalud/shared/types';

export {
  IDIOMA,
  ZONA,
  formatearFecha,
  formatearFechaCivil,
  formatearFechaHora,
  formatearHora,
  formatearNumero,
} from './localizacion';

/**
 * Códigos de `401` que significan que la sesión se perdió, y el `409` del paquete obsoleto.
 *
 * **Ya no se declaran aquí**: vienen del contrato de transporte del paquete compartido, que es el
 * único lugar donde el SPA y el Worker los nombran. Se reexportan con el mismo nombre para que
 * nadie tenga que averiguar de dónde salen, y porque las vistas los importan de este módulo.
 *
 * La distinción que sostienen es la que evita el bucle más obvio del SPA: `POST /auth/login` con la
 * contraseña equivocada también responde `401`, con `invalid_credentials`, y redirigir al ingreso a
 * quien ya está en el ingreso borraría el error del formulario en lugar de mostrarlo. Se despacha
 * por código y no por estado.
 */
export { CODIGO_DE_CLIENTE_OBSOLETO, CODIGOS_DE_SESION_PERDIDA };

/**
 * Código del `403` de la cuenta pendiente de eliminación (criterio 4.6).
 *
 * Es el único de los tres `403` del servidor que no habla de autorización: los otros dos dicen «no
 * puedes tocar los datos de esa persona» y «tu rol no escribe esto», y este dice «tu propia cuenta
 * está en camino de desaparecer». De ahí que tenga código propio y que el SPA lo distinga: el aviso
 * de fallo lo traduce en el estado de la solicitud con su plazo restante en lugar de un muro sin
 * explicación, en cualquier pantalla en que aparezca.
 *
 * Se declara aquí por lo mismo que los otros dos: el límite del monorepo impide que el SPA importe
 * nada de `workers/api`, y el paquete compartido no expone la taxonomía todavía.
 */
export const CODIGO_DE_CUENTA_EN_ELIMINACION = 'account_deletion_pending';

/** Respuesta de error del servidor, con el código estable que el SPA interpreta. */
export class ErrorDeApi extends Error {
  readonly estado: number;
  readonly codigo: string;
  readonly idDePeticion: string;
  readonly detalles: readonly unknown[];

  /**
   * Versión del consentimiento que falta aceptar. Solo llega en el `428` de la compuerta; en
   * cualquier otro error es nulo.
   */
  readonly versionDeConsentimientoPendiente: string | null;

  /**
   * Columnas que el archivo cargado debería traer, cuando el error es el rechazo de un CSV
   * completo (criterio 8.6).
   *
   * Viaja como campo propio del sobre y no en `detalles` porque no es una ruta de campo de un
   * esquema: son los nombres canónicos de un formato de archivo, literales del código del Worker.
   * Se conserva aquí para que la vista de importación pueda decir **cómo** arreglar el archivo, que
   * es la mitad del criterio; sin esto la persona sabe que su archivo no sirve y no sabe por qué.
   * Vacío en cualquier otro error.
   */
  readonly columnasEsperadas: readonly string[];

  /** Columnas obligatorias que faltaban. Vacío cuando el rechazo no es de columnas (8.6). */
  readonly columnasFaltantes: readonly string[];

  /**
   * Instante en que vence el plazo de la eliminación de cuenta, en segundos UTC. Solo llega en el
   * `403` de la cuenta pendiente; en cualquier otro error es nulo (criterio 4.6).
   *
   * Viaja como campo propio del sobre y se conserva aquí porque es **la mitad** de lo que el criterio
   * manda presentar: el código estable dice que el acceso quedó suspendido, y este instante dice
   * hasta cuándo. Sin él la pantalla mostraría un bloqueo sin plazo, que es el error de acceso sin
   * explicación que el criterio prohíbe.
   *
   * Es el vencimiento y no los segundos que quedan: la cuenta atrás la deriva quien la muestra, que
   * es también quien sabe en qué zona la presenta. Lo hace `lib/derechosDeDatos.ts`.
   */
  readonly vencimientoDeLaEliminacion: number | null;

  constructor(
    estado: number,
    codigo: string,
    mensaje: string,
    idDePeticion: string,
    detalles: readonly unknown[] = [],
    versionDeConsentimientoPendiente: string | null = null,
    columnasEsperadas: readonly string[] = [],
    columnasFaltantes: readonly string[] = [],
    vencimientoDeLaEliminacion: number | null = null,
  ) {
    super(mensaje);
    this.name = 'ErrorDeApi';
    this.estado = estado;
    this.codigo = codigo;
    this.idDePeticion = idDePeticion;
    this.detalles = detalles;
    this.versionDeConsentimientoPendiente = versionDeConsentimientoPendiente;
    this.columnasEsperadas = columnasEsperadas;
    this.columnasFaltantes = columnasFaltantes;
    this.vencimientoDeLaEliminacion = vencimientoDeLaEliminacion;
  }

  /** Verdadero cuando la sesión se perdió, y no cuando las credenciales no coinciden. */
  get esSesionPerdida(): boolean {
    return this.estado === 401 && CODIGOS_DE_SESION_PERDIDA.includes(this.codigo);
  }

  /** Verdadero cuando falta aceptar un consentimiento para poder seguir (criterio 4.1). */
  get esConsentimientoPendiente(): boolean {
    return this.estado === 428;
  }

  /** Verdadero cuando el Worker ya no soporta la versión de contrato de este paquete. */
  get esClienteObsoleto(): boolean {
    return this.estado === 409 && this.codigo === CODIGO_DE_CLIENTE_OBSOLETO;
  }

  /**
   * Verdadero cuando el acceso está suspendido porque la propia cuenta está pendiente de
   * eliminación (criterio 4.6).
   *
   * Se distingue por código y no solo por estado, igual que el `401` de sesión perdida: los otros dos
   * `403` del servidor hablan de los datos de otra persona o del rol, y confundirlos con este
   * mostraría «no tienes acceso a esos datos» a quien pidió eliminar los suyos.
   *
   * **No dispara ninguna reacción del cliente.** No hay nada que reintentar ni ninguna pantalla a la
   * que llevar a la persona: la cuenta autentica bien y el bloqueo dura el plazo entero. Lo que
   * corresponde es explicarlo donde aparezca, y eso lo hace el aviso de fallo.
   */
  get esCuentaEnEliminacion(): boolean {
    return this.estado === 403 && this.codigo === CODIGO_DE_CUENTA_EN_ELIMINACION;
  }
}

/**
 * La petición no llegó a destino: sin conexión, o el servidor cortó antes de responder. Es
 * el caso en el que el formulario conserva lo escrito y ofrece reintentar (criterio 26.8).
 */
export class ErrorDeRed extends Error {
  constructor(causa: unknown) {
    super('No pudimos conectar con el servidor. Revisa tu conexión y vuelve a intentarlo.');
    this.name = 'ErrorDeRed';
    this.cause = causa;
  }
}

/** Valor admitido en la cadena de consulta. `undefined` se omite en lugar de viajar vacío. */
type ValorDeConsulta = string | number | boolean | undefined;

/**
 * Cuerpo que viaja **tal cual**, sin pasar por JSON, con su propio tipo de contenido.
 *
 * Existe por una sola ruta: `POST /glucose/import`, cuyo cuerpo **es** el archivo CSV. Envolver
 * diez megabytes de texto en un campo de JSON obligaría a escapar el archivo entero para que el
 * Worker lo desescape y lo vuelva a leer línea por línea, y el número de línea de cada rechazo
 * —que es lo que el criterio 8.2 pide informar— dejaría de corresponder al archivo que la persona
 * cargó.
 */
export interface CuerpoCrudo {
  /** Contenido tal como debe viajar. */
  readonly contenido: BodyInit;
  /** Valor de `content-type`. Se declara siempre: sin él el borde adivina. */
  readonly tipoDeContenido: string;
}

export interface OpcionesDePeticion {
  /** Cuerpo que se serializa como JSON. Ausente en las lecturas. */
  readonly cuerpo?: unknown;
  /**
   * Cuerpo que viaja sin serializar. Excluyente con {@link OpcionesDePeticion.cuerpo}: cuando
   * los dos están presentes manda este, porque el crudo solo se declara a propósito.
   */
  readonly cuerpoCrudo?: CuerpoCrudo;
  /** Parámetros de consulta. Los ausentes no se envían. */
  readonly consulta?: Readonly<Record<string, ValorDeConsulta>>;
  /** Señal de cancelación: la usa el desmontaje del componente y el cambio de vista. */
  readonly senal?: AbortSignal;

  /**
   * Deja pasar el `401` de sesión perdida sin avisar a la reacción, para las dos peticiones
   * que **preguntan** por la sesión en lugar de suponerla.
   *
   * La usa el sondeo de `GET /auth/me` al arrancar: ahí un `401` es la respuesta esperada de
   * quien no ha entrado, y redirigirlo al ingreso desde el propio ingreso agregaría un
   * destino que apunta a la misma pantalla. Es la única excepción y está acotada a una
   * opción explícita: sin ella, toda petición del SPA queda cubierta.
   */
  readonly sinReaccionDeSesion?: boolean;
}

type Metodo = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Lo que el SPA hace ante cada clase de respuesta que no es un error de formulario.
 *
 * Se declara como puerto y lo implementa el armazón, porque las cuatro reacciones necesitan
 * cosas que este módulo no tiene ni debe tener: el enrutador para navegar, el estado de React
 * para los avisos y el flujo de consentimiento para abrirlo. Lo que sí queda aquí es **cuándo**
 * se dispara cada una, que es la parte que no puede quedar repartida entre veinte vistas.
 */
export interface ReaccionesDeApi {
  /**
   * La sesión no está activa (criterio 1.4). Redirige al ingreso conservando la ruta de
   * destino. El error se lanza igual, así que el llamador no sigue como si hubiera datos.
   */
  readonly alPerderLaSesion: (error: ErrorDeApi) => void;

  /**
   * Falta aceptar el consentimiento vigente (criterio 4.1). Abre el flujo de aceptación y
   * resuelve `true` cuando la persona aceptó, con lo que la petición original se repite una
   * vez; `false` cuando la abandonó, y entonces el error se lanza.
   */
  readonly alFaltarConsentimiento: (error: ErrorDeApi) => Promise<boolean>;

  /** El Worker ya no soporta esta versión del paquete: muestra el aviso de recarga. */
  readonly alQuedarObsoletoElCliente: (error: ErrorDeApi) => void;

  /** La petición no llegó (criterio 26.8). Muestra el aviso de conectividad. */
  readonly alFallarLaRed: (error: ErrorDeRed) => void;

  /** Una petición volvió a llegar: retira el aviso de conectividad. */
  readonly alRecuperarLaRed: () => void;
}

/**
 * Reacciones que no hacen nada, y que son las que rigen mientras el armazón no instale las
 * suyas.
 *
 * Es el valor por omisión seguro: sin nada instalado el cliente se comporta como antes de la
 * tarea 4.11 —el error se lanza y el llamador decide— en lugar de fallar por un puerto vacío.
 * `alFaltarConsentimiento` resuelve `false`, que significa «no hay flujo que abrir», y con eso
 * el `428` no se reintenta.
 */
const SIN_REACCIONES: ReaccionesDeApi = {
  alPerderLaSesion: () => undefined,
  alFaltarConsentimiento: () => Promise.resolve(false),
  alQuedarObsoletoElCliente: () => undefined,
  alFallarLaRed: () => undefined,
  alRecuperarLaRed: () => undefined,
};

let reacciones: ReaccionesDeApi = SIN_REACCIONES;

/** Verdadero mientras el último fallo conocido siga siendo de red, para avisar al recuperar. */
let laRedEstabaCaida = false;

/**
 * Instala las reacciones del SPA. Devuelve la función que las retira, que es lo que el efecto
 * de React llama al desmontar y lo que cada prueba usa para no dejar estado entre casos.
 *
 * Instalar dos veces reemplaza: no hay una lista de suscriptores porque no hay dos partes del
 * SPA que puedan reaccionar a lo mismo sin pisarse.
 *
 * @param nuevas reacciones del armazón
 * @returns función que restituye el comportamiento sin reacciones
 */
export function instalarReaccionesDeApi(nuevas: ReaccionesDeApi): () => void {
  reacciones = nuevas;
  return () => {
    if (reacciones === nuevas) reacciones = SIN_REACCIONES;
  };
}

/**
 * Comprueba que el camino sea relativo antes de tocar la red.
 *
 * Rechaza el camino sin barra inicial, el que empieza con doble barra —que el navegador lee
 * como otro origen— y el que trae esquema. Sin esta comprobación, un camino armado con un
 * valor del servidor podría convertir una llamada de mismo origen en una llamada a otro
 * dominio, y la cookie de sesión no es el único problema: el destino tampoco sería el
 * esperado.
 */
function exigirCaminoRelativo(camino: string): void {
  const esRelativo = camino.startsWith('/') && !camino.startsWith('//') && !camino.includes('://');

  if (!esRelativo) {
    throw new Error(
      `El cliente de API solo pide rutas relativas al mismo origen; recibió «${camino}».`,
    );
  }
}

function construirConsulta(parametros: OpcionesDePeticion['consulta']): string {
  if (parametros === undefined) return '';

  const busqueda = new URLSearchParams();
  for (const [nombre, valor] of Object.entries(parametros)) {
    if (valor !== undefined) busqueda.set(nombre, String(valor));
  }

  const texto = busqueda.toString();
  return texto === '' ? '' : `?${texto}`;
}

/** Lee el cuerpo como JSON y devuelve `undefined` si no lo es, en lugar de lanzar. */
async function leerJson(respuesta: Response): Promise<unknown> {
  const tipo = respuesta.headers.get('content-type') ?? '';
  if (!tipo.includes('application/json')) return undefined;

  try {
    return await respuesta.json();
  } catch {
    return undefined;
  }
}

/** Reconoce el sobre de error del Worker sin confiar en que la respuesta lo traiga. */
function esSobreDeError(cuerpo: unknown): cuerpo is SobreDeError {
  if (typeof cuerpo !== 'object' || cuerpo === null || !('error' in cuerpo)) return false;

  const { error } = cuerpo as { error: unknown };
  return (
    typeof error === 'object' &&
    error !== null &&
    typeof (error as { code?: unknown }).code === 'string' &&
    typeof (error as { message?: unknown }).message === 'string'
  );
}

function errorDesde(respuesta: Response, cuerpo: unknown): ErrorDeApi {
  const idDeCabecera = respuesta.headers.get(CABECERAS.idDePeticion) ?? '';

  if (esSobreDeError(cuerpo)) {
    const {
      code,
      message,
      requestId,
      details,
      pendingConsentVersion,
      expectedColumns,
      missingColumns,
      deletionDueAt,
    } = cuerpo.error;
    return new ErrorDeApi(
      respuesta.status,
      code,
      message,
      requestId ?? idDeCabecera,
      details,
      pendingConsentVersion ?? null,
      expectedColumns ?? [],
      missingColumns ?? [],
      deletionDueAt ?? null,
    );
  }

  // Sin sobre no hay código estable que interpretar: puede ser un fallo del borde antes de
  // llegar al Worker. Se sintetiza uno derivado del estado para no perder la distinción.
  return new ErrorDeApi(
    respuesta.status,
    `http_${String(respuesta.status)}`,
    'El servidor respondió de una forma que no esperábamos. Vuelve a intentarlo.',
    idDeCabecera,
  );
}

/**
 * Núcleo de toda petición: las cabeceras del contrato, el camino relativo y el despacho de las
 * cuatro respuestas que no son un error de formulario. Devuelve la respuesta **correcta**.
 *
 * Se separó de {@link pedir} para que la exportación de la serie pueda leer el cuerpo como archivo
 * sin repetir ni una línea del despacho: `GET /glucose/export` responde `200` con un CSV o `202`
 * con JSON, y una segunda función que hiciera su propio `fetch` sería una segunda función que se
 * olvida del `401`.
 */
async function pedirRespuesta(
  metodo: Metodo,
  camino: string,
  opciones: OpcionesDePeticion = {},
  yaSeReintento = false,
): Promise<Response> {
  exigirCaminoRelativo(camino);

  const cabeceras = new Headers({
    accept: 'application/json',
    'accept-language': IDIOMA_CHILE,
    // El valor es indiferente; lo que cuenta es que un formulario de otro sitio no puede
    // añadir esta cabecera y una petición de otro origen necesitaría un comprobante previo.
    [CABECERAS.peticionDeAplicacion]: '1',
    [CABECERAS.versionDeCliente]: SHARED_CONTRACT_VERSION,
  });

  const init: RequestInit = {
    method: metodo,
    headers: cabeceras,
    // La cookie viaja porque el origen es el propio; a otro origen no iría, y no hay otro.
    credentials: 'same-origin',
    // Ninguna respuesta de la API se sirve desde la caché del navegador: son datos de salud
    // que cambian, y una lectura vieja es peor que una lectura lenta.
    cache: 'no-store',
  };

  if (opciones.cuerpoCrudo !== undefined) {
    cabeceras.set('content-type', opciones.cuerpoCrudo.tipoDeContenido);
    init.body = opciones.cuerpoCrudo.contenido;
  } else if (opciones.cuerpo !== undefined) {
    cabeceras.set('content-type', 'application/json');
    init.body = JSON.stringify(opciones.cuerpo);
  }

  if (opciones.senal !== undefined) init.signal = opciones.senal;

  let respuesta: Response;
  try {
    respuesta = await fetch(`${PREFIJO_API}${camino}${construirConsulta(opciones.consulta)}`, init);
  } catch (causa) {
    // Una cancelación deliberada no es un fallo de red: se propaga tal cual para que el
    // llamador la reconozca y no muestre un aviso de conectividad que no corresponde.
    if (opciones.senal?.aborted === true) throw causa;

    const error = new ErrorDeRed(causa);
    laRedEstabaCaida = true;
    // Se avisa y se lanza, **sin reintentar**. Reintentar por cuenta propia una mutación que
    // pudo haber llegado duplicaría el registro; el criterio 26.8 pide conservar lo escrito y
    // ofrecer reintentar, y quien reintenta es la persona desde el formulario, que conserva
    // sus datos porque el estado vive en el componente y nadie lo limpia en el fallo.
    reacciones.alFallarLaRed(error);
    throw error;
  }

  // La petición llegó: si el último fallo conocido era de red, el aviso de conectividad ya no
  // corresponde. Se retira aquí y no en un escucha de `online` del navegador porque lo que
  // importa no es tener interfaz de red sino que el servidor conteste, y eso solo lo sabe una
  // respuesta.
  if (laRedEstabaCaida) {
    laRedEstabaCaida = false;
    reacciones.alRecuperarLaRed();
  }

  if (!respuesta.ok) {
    const error = errorDesde(respuesta, await leerJson(respuesta));

    if (error.esSesionPerdida) {
      if (opciones.sinReaccionDeSesion !== true) reacciones.alPerderLaSesion(error);
      throw error;
    }

    if (error.esClienteObsoleto) {
      reacciones.alQuedarObsoletoElCliente(error);
      throw error;
    }

    // El `428` es el único que puede terminar bien: se abre el flujo de aceptación y, si la
    // persona acepta, se repite la petición original **una sola vez**. El interruptor es un
    // parámetro y no un contador porque una segunda ronda solo puede significar que el
    // servidor sigue exigiendo consentimiento después de aceptarlo, y en ese caso reintentar
    // es un bucle y no una recuperación.
    if (error.esConsentimientoPendiente && !yaSeReintento) {
      const acepto = await reacciones.alFaltarConsentimiento(error);
      if (acepto) return pedirRespuesta(metodo, camino, opciones, true);
    }

    throw error;
  }

  return respuesta;
}

async function pedir<T>(
  metodo: Metodo,
  camino: string,
  opciones: OpcionesDePeticion = {},
): Promise<T> {
  const respuesta = await pedirRespuesta(metodo, camino, opciones);
  // `204 Sin contenido` es una respuesta válida de las eliminaciones: el llamador la declara
  // como `void` y aquí no hay nada que interpretar.
  return (await leerJson(respuesta)) as T;
}

/**
 * Los nombres son los verbos del protocolo y no su traducción, igual que `fetch`, `Headers` o
 * `Response`: nombran el transporte, no el dominio.
 */
export const apiClient = {
  get: <T>(camino: string, opciones?: OpcionesDePeticion): Promise<T> =>
    pedir<T>('GET', camino, opciones),
  post: <T>(camino: string, opciones?: OpcionesDePeticion): Promise<T> =>
    pedir<T>('POST', camino, opciones),
  put: <T>(camino: string, opciones?: OpcionesDePeticion): Promise<T> =>
    pedir<T>('PUT', camino, opciones),
  patch: <T>(camino: string, opciones?: OpcionesDePeticion): Promise<T> =>
    pedir<T>('PATCH', camino, opciones),
  delete: <T = void>(camino: string, opciones?: OpcionesDePeticion): Promise<T> =>
    pedir<T>('DELETE', camino, opciones),

  /**
   * La respuesta correcta sin interpretar, para las rutas cuyo cuerpo no es JSON.
   *
   * La usa la exportación de la serie, que responde `200` con un CSV descargable o `202` con la
   * constancia en JSON de que el archivo se está armando: el llamador mira el estado y decide cómo
   * leerlo. Todo lo demás —cabeceras del contrato, camino relativo, `401`, `409`, `428` y fallo de
   * red— es exactamente lo mismo que en las otras cinco, porque es el mismo núcleo.
   */
  respuesta: (metodo: Metodo, camino: string, opciones?: OpcionesDePeticion): Promise<Response> =>
    pedirRespuesta(metodo, camino, opciones),
} as const;
